<script setup>
import { ref, computed, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import _find from "lodash/find";
import _groupBy from "lodash/groupBy";
import _debounce from "lodash/debounce";
import _uniqueId from "lodash/uniqueId";
import _clone from "lodash/clone";
import { useForm } from "@/form";
import { Combobox, ComboboxButton, ComboboxInput, ComboboxLabel, ComboboxOption, ComboboxOptions } from "@headlessui/vue";
import { ChevronUpDownIcon } from "@heroicons/vue/20/solid";
import { FormErrors } from "@Components/Form";
import { twMerge } from "tailwind-merge";

const props = defineProps({
    modelValue: Number,
    initialPost: Object,
    postTypes: Array,
    errors: {
        type: Array,
        default: [],
    },
});
const emit = defineEmits(["update:modelValue"]);

const { t: __ } = useI18n();

const processing = ref(false);
const posts = ref([]);
const selectedPost = ref();
const currentFetchId = ref();
const form = useForm({
    query: "",
});

const groupedPosts = computed(() => {
    return _groupBy(posts.value, "post_type");
});

const displayValue = () => {
    if (selectedPost.value) {
        return selectedPost.value.post_title;
    }
};

const fetchPosts = _debounce(async () => {
    if (form.query.length > 0) {
        const _currentFetchId = _uniqueId("fetch_");
        currentFetchId.value = _clone(_currentFetchId);

        processing.value = true;
        const data = await form.post("linking_manual_search_posts");
        nextTick(() => {
            if (currentFetchId.value === _currentFetchId) {
                posts.value = data;
                processing.value = false;
            }
        });
    }
}, 500);

const setQuery = (value) => {
    form.query = value;

    if (form.query.length > 0) {
        fetchPosts();
    }
};

const selectPost = (id) => {
    selectedPost.value = _find(posts.value, ["ID", id]);
    emit("update:modelValue", selectedPost.value);
};

const buttonClasses = computed(() => {
    let classes = [
        "block w-full rounded-md border-0 py-1.5 pl-3 pr-14 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset text-sm leading-6",
        "text-gray-900 bg-white ring-gray-300 focus:ring-gray-950 placeholder:text-gray-400 focus-visible:outline-none",
    ];

    classes.push("dark:text-white dark:bg-gray-800 dark:ring-gray-700 dark:focus:ring-gray-600 dark:placeholder:text-gray-400");

    if (props.errors.length > 0) {
        classes.push("ring-red-500 focus:ring-red-500 dark:ring-red-500 dark:focus:ring-red-500");
    }

    return twMerge(classes);
});

const optionsClasses = computed(() => {
    let classes = [
        "absolute z-20 w-full mt-1 overflow-auto text-sm rounded-md shadow-lg max-h-60 ring-1 ring-opacity-5 focus:outline-none",
        "bg-white ring-black",
    ];

    classes.push("dark:bg-gray-800 dark:ring-gray-700");

    return twMerge(classes);
});

const activeOptionClasses = computed(() => {
    let classes = ["bg-gray-50"];

    classes.push("dark:bg-gray-700");

    return twMerge(classes);
});

if (props.initialPost) {
    form.query = props.initialPost.post_title;
    selectedPost.value = props.initialPost;
}
</script>

<template>
    <Combobox as="div" :modelValue="modelValue" @update:modelValue="selectPost" nullable>
        <ComboboxLabel class="text-sm font-medium leading-6 text-gray-900 dark:text-white">{{ __("Post à lier") }}</ComboboxLabel>
        <div class="relative mt-2">
            <ComboboxInput
                :class="buttonClasses"
                @change="setQuery($event.target.value)"
                :display-value="displayValue"
                :placeholder="__('Recherchez un titre de post')" />
            <div v-if="processing" class="absolute inset-y-0 right-0 flex items-center px-2 mr-5 text-gray-400 rounded-r-md focus:outline-none">
                <svg class="-ml-1 w-4 h-4 mr-2.5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>
            <ComboboxButton class="absolute inset-y-0 right-0 flex items-center px-2 rounded-r-md focus:outline-none">
                <ChevronUpDownIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
            </ComboboxButton>

            <ComboboxOptions v-if="posts.length > 0" :class="optionsClasses">
                <template v-for="(groupedPost, postType) in groupedPosts">
                    <div class="sticky top-0 z-10 px-4 py-2 text-gray-900 bg-gray-50 dark:text-white dark:bg-gray-700">
                        {{ _find(postTypes, ["name", postType])?.label ?? postType }}
                    </div>
                    <ComboboxOption v-for="post in groupedPost" :key="post.ID" :value="post.ID" as="template" v-slot="{ active, selected }">
                        <li :class="['p-1 text-gray-900 dark:text-white relative cursor-default select-none']">
                            <div :class="[active ? activeOptionClasses : '', 'rounded-sm py-2 px-3 flex items-center']">
                                <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']"> {{ post.post_title }} (#{{ post.ID }})</span>
                            </div>
                        </li>
                    </ComboboxOption>
                </template>
            </ComboboxOptions>
        </div>

        <FormErrors v-if="errors" class="mt-2" :errors="errors" />
    </Combobox>
</template>
